import { useState, useRef } from "react";

const FORM_URL = "https://readdy.ai/api/form/d7cg26nsch5bvrijf2jg";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus("sending");
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="w-full py-24 px-6 md:px-16" style={{ background: "#0f2a2a" }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs font-semibold tracking-[0.3em] text-emerald-400/70 uppercase mb-4">
            Get In Touch
          </p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight mb-4">
            Contact Us
          </h2>
          <p className="text-white/50 text-base max-w-md mx-auto">
            Have a question or inquiry? Reach out to the FUSA d.o.o. team and we&apos;ll get back to you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact info */}
          <div className="flex flex-col gap-8">
            <div>
              <h3 className="text-white font-bold text-lg mb-6">Company Information</h3>
              <div className="flex flex-col gap-5">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-400/10 shrink-0">
                    <i className="ri-map-pin-line text-emerald-400 text-lg" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Address</p>
                    <p className="text-white font-medium">Trg izgnancev 2</p>
                    <p className="text-white/60 text-sm">City: Brezice</p>
                    <p className="text-white/60 text-sm">Zip: 8250</p>
                    <p className="text-white/60 text-sm">Country: Slovenia</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-400/10 shrink-0">
                    <i className="ri-mail-line text-emerald-400 text-lg" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Email</p>
                    <p className="text-white font-medium">contact@fusadoo.com</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-400/10 shrink-0">
                    <i className="ri-phone-line text-emerald-400 text-lg" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Number</p>
                    <p className="text-white font-medium">+1 6416706817</p>
                  </div>
                </div>
              </div>
            </div>


          </div>

          {/* Contact form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
            <h3 className="text-white font-bold text-lg mb-6">Send a Message</h3>

            {status === "success" ? (
              <div className="flex flex-col items-center justify-center h-64 text-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-400/20">
                  <i className="ri-check-line text-emerald-400 text-3xl" />
                </div>
                <p className="text-white font-semibold text-lg">Message Sent!</p>
                <p className="text-white/50 text-sm">We&apos;ll get back to you as soon as possible.</p>
                <button
                  onClick={() => setStatus("idle")}
                  className="text-emerald-400 text-sm underline cursor-pointer hover:text-emerald-300 transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest mb-2 block">Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your full name"
                      className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-emerald-400/50 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest mb-2 block">Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-emerald-400/50 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/50 text-xs uppercase tracking-widest mb-2 block">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    placeholder="How can we help?"
                    className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-emerald-400/50 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-white/50 text-xs uppercase tracking-widest mb-2 block">
                    Message
                    <span className={`ml-2 normal-case tracking-normal ${charCount > 500 ? "text-red-400" : "text-white/30"}`}>
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    placeholder="Write your message here..."
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-emerald-400/50 transition-colors resize-none"
                  />
                  {charCount > 500 && (
                    <p className="text-red-400 text-xs mt-1">Message exceeds 500 character limit.</p>
                  )}
                </div>

                {status === "error" && (
                  <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending" || charCount > 500}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl py-3.5 text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === "sending" ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/f4c724e7-9728-4ad3-9ee3-58e081299879.png"
              alt="FUSA d.o.o."
              className="h-8 w-auto object-contain"
            />
            <span className="text-white/50 text-sm">FUSA d.o.o.</span>
          </div>
          <p className="text-white/30 text-xs text-center">
            &copy; {new Date().getFullYear()} FUSA d.o.o. All rights reserved. iOS app development specialists.
          </p>
          <p className="text-white/30 text-xs">Brezice, Slovenia</p>
        </div>
      </div>
    </section>
  );
}
